import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from 'environments/environment';

/**
 * Thin, injectable facade over the Datadog RUM SDK.
 *
 * The rest of the app depends on this service, NOT on `@datadog/browser-rum` directly,
 * so the vendor SDK has a single owner (this module) and instrumentation can be added
 * anywhere without repeating enable-guards. Every method no-ops when RUM is disabled
 * (dev / ci / mock, i.e. environment.useDatadogRum === false); consumers can replace the
 * whole service with a stub in their own tests.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
    private get enabled(): boolean {
        return environment.useDatadogRum;
    }

    /** Report a handled error with optional domain context. */
    trackError(error: Error, context?: Record<string, unknown>): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addError(error, context);
    }

    /** Record a custom business action / event (e.g. 'quote_submitted'). */
    trackAction(name: string, context?: Record<string, unknown>): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addAction(name, context);
    }

    /** Record a custom timing / journey milestone relative to the current view. */
    addTiming(name: string, time?: number): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.addTiming(name, time);
    }

    /** Attach the authenticated user (IDs / roles only — never PII). */
    setUser(user: { id: string; [key: string]: unknown }): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.setUser(user);
    }

    /** Clear the user on logout. */
    clearUser(): void {
        if (!this.enabled) {
            return;
        }
        datadogRum.clearUser();
    }
}
